import React, { useState, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { CATEGORIES, UNITS } from './ProductForm';

/**
 * Product Importer Component
 * CSV/Excel'den toplu ürün import
 *
 * @param {function} onComplete - Import tamamlandığında
 * @param {function} onCancel - İptal edildiğinde
 */
export default function ProductImporter({ onComplete, onCancel }) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const createProductMutation = useMutation({
    mutationFn: (data) => base44.entities.Product.create(data),
  });

  // CSV parse
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV muss mindestens eine Kopfzeile und eine Datenzeile haben');
    }

    const headers = lines[0].split(';').map(h => h.trim().toLowerCase());
    const requiredHeaders = ['name', 'sku', 'price'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

    if (missingHeaders.length > 0) {
      throw new Error(`Fehlende Spalten: ${missingHeaders.join(', ')}`);
    }

    const data = [];
    const parseErrors = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';').map(v => v.trim());
      const row = {};

      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      // Validierung
      const rowErrors = [];
      if (!row.name) rowErrors.push('Name fehlt');
      if (!row.sku) rowErrors.push('SKU fehlt');
      if (!row.price || isNaN(parseFloat(row.price))) rowErrors.push('Ungültiger Preis');

      if (rowErrors.length > 0) {
        parseErrors.push({ row: i + 1, errors: rowErrors, data: row });
      } else {
        data.push({
          name: row.name,
          sku: row.sku.toUpperCase(),
          price: parseFloat(row.price.replace(',', '.')),
          category: row.category || row.kategorie || 'Sonstiges',
          unit: row.unit || row.einheit || 'Stück',
          description: row.description || row.beschreibung || '',
          min_order_quantity: parseInt(row.min_order_quantity || row.mindestmenge) || 1,
          stock_status: 'In Stock',
          is_active: true,
        });
      }
    }

    return { data, errors: parseErrors };
  };

  // Dosya seçimi
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Dosya tipi kontrolü
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const isValidType = validTypes.includes(selectedFile.type) || selectedFile.name.endsWith('.csv');

    if (!isValidType) {
      toast.error('Nur CSV-Dateien werden unterstützt');
      return;
    }

    setFile(selectedFile);
    setParsedData([]);
    setErrors([]);
    setImportResults(null);

    // Dosyayı oku ve parse et
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const result = parseCSV(event.target.result);
        setParsedData(result.data);
        setErrors(result.errors);

        if (result.data.length > 0) {
          toast.success(`${result.data.length} Produkte erkannt`);
        }
      } catch (error) {
        toast.error(error.message);
        setFile(null);
      }
    };
    reader.readAsText(selectedFile);
  };

  // Import başlat
  const startImport = async () => {
    if (parsedData.length === 0) return;

    setIsImporting(true);
    setImportProgress(0);

    const results = {
      success: 0,
      failed: 0,
      errors: [],
    };

    for (let i = 0; i < parsedData.length; i++) {
      const product = parsedData[i];

      try {
        await createProductMutation.mutateAsync(product);
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push({
          product: product.name,
          error: error.message,
        });
      }

      setImportProgress(Math.round(((i + 1) / parsedData.length) * 100));
    }

    setImportResults(results);
    setIsImporting(false);
    queryClient.invalidateQueries(['products']);

    if (results.success > 0) {
      toast.success(`${results.success} Produkte erfolgreich importiert`);
    }
    if (results.failed > 0) {
      toast.error(`${results.failed} Produkte fehlgeschlagen`);
    }
  };

  // Şablon indir
  const downloadTemplate = () => {
    const template = `name;sku;price;category;unit;description;min_order_quantity
Tomatensoße Premium;SOS-TOM001;4.99;Saucen;Stück;Hochwertige Tomatensoße;1
Olivenöl Extra Vergine;OEL-OLI001;12.50;Öle;Liter;Italienisches Olivenöl;1
Basmatireis 5kg;REI-BAS001;8.99;Trockenwaren;Packung;Premium Basmati;1`;

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'produkt_import_vorlage.csv';
    link.click();
  };

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Produkte importieren
          </CardTitle>
          <p className="text-sm text-slate-500">
            Laden Sie eine CSV-Datei mit Produktdaten hoch
          </p>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={downloadTemplate} className="w-full">
            <Download className="w-4 h-4 mr-2" />
            CSV-Vorlage herunterladen
          </Button>
        </CardContent>
      </Card>

      {/* File Upload */}
      <Card
        className={cn(
          'border-2 border-dashed transition-colors cursor-pointer',
          file ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 hover:border-slate-300'
        )}
        onClick={() => fileInputRef.current?.click()}
      >
        <CardContent className="py-8">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="text-center">
            {file ? (
              <>
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                <p className="font-medium text-emerald-700">{file.name}</p>
                <p className="text-sm text-slate-500 mt-1">
                  {parsedData.length} Produkte bereit zum Import
                </p>
              </>
            ) : (
              <>
                <Upload className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                <p className="font-medium text-slate-700">CSV-Datei auswählen</p>
                <p className="text-sm text-slate-500 mt-1">
                  oder per Drag & Drop hier ablegen
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Parse Errors */}
      {errors.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              {errors.length} Zeilen mit Fehlern (werden übersprungen)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {errors.slice(0, 5).map((err, i) => (
                <p key={i} className="text-xs text-amber-700">
                  Zeile {err.row}: {err.errors.join(', ')}
                </p>
              ))}
              {errors.length > 5 && (
                <p className="text-xs text-amber-600">
                  ... und {errors.length - 5} weitere Fehler
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {parsedData.length > 0 && !importResults && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Vorschau (erste 5 Produkte)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {parsedData.slice(0, 5).map((product, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-xs text-slate-500">{product.sku} • {product.category}</p>
                  </div>
                  <p className="font-semibold">€{product.price.toFixed(2)}</p>
                </div>
              ))}
              {parsedData.length > 5 && (
                <p className="text-xs text-slate-500 text-center">
                  ... und {parsedData.length - 5} weitere Produkte
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Progress */}
      {isImporting && (
        <Card>
          <CardContent className="py-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Import läuft...</span>
                <span className="font-medium">{importProgress}%</span>
              </div>
              <Progress value={importProgress} className="h-2" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Results */}
      {importResults && (
        <Card className={importResults.failed > 0 ? 'border-amber-200' : 'border-emerald-200'}>
          <CardContent className="py-6">
            <div className="text-center space-y-2">
              {importResults.failed === 0 ? (
                <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
              ) : (
                <AlertCircle className="w-12 h-12 mx-auto text-amber-500" />
              )}
              <p className="font-medium">Import abgeschlossen</p>
              <div className="flex justify-center gap-4 text-sm">
                <span className="text-emerald-600">
                  <CheckCircle2 className="w-4 h-4 inline mr-1" />
                  {importResults.success} erfolgreich
                </span>
                {importResults.failed > 0 && (
                  <span className="text-red-600">
                    <XCircle className="w-4 h-4 inline mr-1" />
                    {importResults.failed} fehlgeschlagen
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={onCancel} disabled={isImporting} className="flex-1">
          <X className="w-4 h-4 mr-2" />
          {importResults ? 'Schließen' : 'Abbrechen'}
        </Button>
        {!importResults && (
          <Button
            onClick={startImport}
            disabled={parsedData.length === 0 || isImporting}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            {isImporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importiere...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {parsedData.length} Produkte importieren
              </>
            )}
          </Button>
        )}
        {importResults && (
          <Button onClick={onComplete} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Fertig
          </Button>
        )}
      </div>
    </div>
  );
}
